import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { suggestionsAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';

export interface Suggestion {
  id: string;
  title: string;
  description: string;
  type: 'SUGGESTION' | 'PROBLEM';
  module: string;
  status: string;
  conclusion?: string;
  conclusionDate?: string;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ListParams {
  page?: number;
  limit?: number;
  status?: string;
  type?: 'SUGGESTION' | 'PROBLEM';
  userId?: string;
}

interface CreateSuggestionData {
  title: string;
  description: string;
  type: 'SUGGESTION' | 'PROBLEM';
  module: string;
}

/**
 * Hook para listar sugestões
 */
export function useSuggestions(params?: ListParams) {
  return useQuery({
    queryKey: ['suggestions', params],
    queryFn: async () => {
      const response = await suggestionsAPI.list(params);
      return {
        suggestions: response.data.data as Suggestion[],
        pagination: response.data.pagination,
      };
    },
  });
}

/**
 * Hook para criar sugestão
 */
export function useCreateSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSuggestionData) => {
      const response = await suggestionsAPI.create(data);
      return response.data.data as Suggestion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] });
      toast.success('Sugestão criada com sucesso!');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Erro ao criar sugestão';
      toast.error(message);
    },
  });
}

/**
 * Hook para atualizar status (apenas admin)
 */
export function useUpdateSuggestionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, conclusion }: { id: string; status: string; conclusion?: string }) => {
      const response = await suggestionsAPI.updateStatus(id, status, conclusion);
      return response.data.data as Suggestion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] });
      toast.success('Status atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Erro ao atualizar status';
      toast.error(message);
    },
  });
}

/**
 * Hook para deletar sugestão
 */
export function useDeleteSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await suggestionsAPI.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suggestions'] });
      toast.success('Sugestão deletada com sucesso!');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Erro ao deletar sugestão';
      toast.error(message);
    },
  });
}
